import { Link, matchPath, useLocation } from 'react-router-dom'

type Crumb = {
  label: string
  path?: string
}

// Map known static paths to human-friendly labels
const TITLE_MAP: Record<string, string> = {
  '/': 'Home',
  '/docs': 'Documentation',
  '/docs/api-docs': 'API Specifications',
  '/docs/app-integration-development': 'Integration & App Development',
  '/integrations': 'Integrations',
}

// For dynamic segments where we know friendly titles
const API_TITLES: Record<string, string> = {
  'qplay-rest': 'Q-Play Rest API',
  'qplay-partner': 'Q-Play Partner API',
  'qcal-rest': 'Q-Cal Rest API',
}

export default function Breadcrumbs() {
  const location = useLocation()
  const { pathname } = location

  // Root shows just "Home" as the current page.
  if (pathname === '/') {
    return (
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <ol>
          <li aria-current="page"><span>Home</span></li>
        </ol>
      </nav>
    )
  }

  const parts = pathname.split('/').filter(Boolean)

  const crumbs: Crumb[] = []
  // Always start with Home
  crumbs.push({ label: TITLE_MAP['/'], path: '/' })

  let acc = ''
  parts.forEach((part, index) => {
    acc += '/' + part

    // Try to use known titles first
    let label: string | undefined = TITLE_MAP[acc]
    // Allow overriding the target path for specific crumbs (e.g., link APIs to root)
    let targetPath: string | undefined

    // Handle dynamic matches like /docs/:slug and /docs/api-docs/:apiId
    if (!label) {
      if (matchPath({ path: '/docs/:slug' }, acc)) {
        if (index === 0) {
          // First segment docs gets the parent label
          label = TITLE_MAP['/docs']
        } else {
          label = prettify(part)
        }
      } else if (matchPath({ path: '/docs/api-docs/:apiId' }, acc)) {
        const apiId = part
        label = API_TITLES[apiId] || prettify(apiId)
      }
    }

    // Fallback to a prettified segment
    if (!label) label = prettify(part)

    const isLast = index === parts.length - 1
    crumbs.push({
      label,
      // Only link non-final items. If we have a targetPath override, use it instead of acc.
      path: isLast ? undefined : targetPath || acc,
    })
  })

  // When deep, keep Home + the last two so "Home" is never clipped off the left.
  const shown = crumbs.length > 3 ? [crumbs[0], ...crumbs.slice(-2)] : crumbs

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol>
        {shown.map((c, i) => {
          const isLast = i === shown.length - 1
          return (
            <li key={i} aria-current={isLast ? 'page' : undefined}>
              {c.path && !isLast ? <Link to={c.path}>{c.label}</Link> : <span>{c.label}</span>}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function prettify(segment: string) {
  // Replace dashes with spaces and capitalize words
  return segment
    .split('-')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}
